import type {
	AiChatRatingReview,
	AiCopilotMode,
	AiCopilotResponse,
	AiRoutingAnalysis,
	CannedResponseSummary,
	ChatMessage,
	ConversationStatus,
	ConversationSummary,
	ConversationTransferOptions,
	MembershipSummary,
	VisitorSummary,
	WidgetBubbleAppearance,
	WidgetBubbleShape,
} from '@livechat/shared';
import {
	type CSSProperties,
	type FormEvent,
	type KeyboardEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { io, type Socket } from 'socket.io-client';

import {
	analyseConversationRouting,
	askConversationAi,
	assignConversation,
	blockConversationVisitor,
	closeConversation,
	currentAccessToken,
	emailConversationTranscript,
	getConversation,
	getConversationAiRatingReview,
	getConversationTransferOptions,
	getLatestConversationRoutingAnalysis,
	getMemberProfile,
	getVisitor,
	helpWithConversationAnswer,
	listCannedResponses,
	listConversationMessages,
	listConversations,
	onAccessTokenChanged,
	pushConversationVisitor,
	realtimeUrl,
	refreshSession,
	recordConversationCopilotFeedback,
	recordConversationRoutingFeedback,
	sendConversationMessage,
	suggestConversationReply,
	summarizeConversation,
	transferConversation,
} from '../api.js';
import {
	filterCannedResponses,
	findSlashTrigger,
	insertCannedResponse,
	isCannedSelectionKey,
	type SlashTrigger,
} from '../canned-responses.js';
import { assignedTeamMemberLabel } from '../member-label.js';
import { useDateFormat } from '../date-format.js';
import { CHAT_EMOJIS, insertEmoji } from '../emoji-picker.js';
import { messageThreadAtBottom } from '../message-thread.js';
import { createRefreshRunner } from '../refresh-runner.js';
import {
	copilotEditDistance,
	replyDraftCanSend,
	type ReplyDraftContext,
	shouldSubmitReply,
} from '../reply-composer.js';
import { sortedWaitingChats, waitingDuration } from '../waiting-chats.js';
import { visitorCompanyLabel } from '../visitor-company.js';
import { openCobrowseWindow } from '../cobrowse-window.js';
import { CannedResponseMenu } from './CannedResponseMenu.js';
import { MemberAvatar } from './MemberAvatar.js';
import { CountryFlag, visitorLocationLabel } from './VisitorLocation.js';
import { VisitorLocationMap } from './VisitorLocationMap.js';

type InboxFilter = ConversationStatus | 'all';
type CurrentChatScope = 'mine' | 'all';
type ConversationAction = 'block' | 'email' | 'push';

function routingEntityLabel(type: AiRoutingAnalysis['entities'][number]['type']): string {
	return type.replaceAll('_', ' ');
}

function routingValueLabel(value: string): string {
	return value.slice(0, 1).toUpperCase() + value.slice(1);
}

function currentChatScopeStorageKey(tenantId: string, userId: string): string {
	return `livechat:current-chat-scope:${tenantId}:${userId}`;
}

function storedCurrentChatScope(tenantId: string, userId: string): CurrentChatScope {
	if (typeof window === 'undefined') return 'mine';
	try {
		return window.localStorage.getItem(currentChatScopeStorageKey(tenantId, userId)) === 'all'
			? 'all'
			: 'mine';
	} catch {
		return 'mine';
	}
}

interface TypingPayload {
	conversationId: string;
	draft?: string;
	isTyping: boolean;
	senderType: 'agent' | 'visitor';
}

interface UsedCopilotSuggestion {
	conversationId: string;
	message: string;
	runId: string;
}

function bubbleRadius(shape: WidgetBubbleShape, radius: number, rightAligned: boolean): string {
	if (shape === 'speech') {
		return rightAligned
			? `${radius}px ${Math.min(6, radius)}px ${radius}px ${radius}px`
			: `${Math.min(6, radius)}px ${radius}px ${radius}px ${radius}px`;
	}
	return `${radius}px`;
}

function bubbleVariables(appearance?: {
	agentBubble: WidgetBubbleAppearance;
	visitorBubble: WidgetBubbleAppearance;
}): CSSProperties {
	if (!appearance) return {};
	return {
		'--chat-agent-background': appearance.agentBubble.backgroundColour,
		'--chat-agent-font-size': `${appearance.agentBubble.fontSize}px`,
		'--chat-agent-radius': bubbleRadius(
			appearance.agentBubble.shape,
			appearance.agentBubble.radius,
			true,
		),
		'--chat-agent-text': appearance.agentBubble.textColour,
		'--chat-visitor-background': appearance.visitorBubble.backgroundColour,
		'--chat-visitor-font-size': `${appearance.visitorBubble.fontSize}px`,
		'--chat-visitor-radius': bubbleRadius(
			appearance.visitorBubble.shape,
			appearance.visitorBubble.radius,
			false,
		),
		'--chat-visitor-text': appearance.visitorBubble.textColour,
	} as CSSProperties;
}

function mergeConversation(
	current: ConversationSummary[],
	conversation: ConversationSummary,
	filter: InboxFilter,
): ConversationSummary[] {
	const remaining = current.filter((item) => item.id !== conversation.id);
	if (filter !== 'all' && conversation.status !== filter) {
		return remaining;
	}
	return [conversation, ...remaining].sort((left, right) =>
		(right.lastMessageAt ?? right.createdAt).localeCompare(
			left.lastMessageAt ?? left.createdAt,
		),
	);
}

function mergeMessage(current: ChatMessage[], message: ChatMessage): ChatMessage[] {
	if (current.some((item) => item.id === message.id)) {
		return current;
	}
	return [...current, message].sort((left, right) => left.sequence - right.sequence);
}

function ConversationAgentAvatar({
	conversation,
	size = 'small',
}: {
	conversation: ConversationSummary;
	size?: 'small' | 'medium';
}) {
	const name = conversation.assignedUserName;
	if (!name) return null;
	if (conversation.assignedAvatarEnabled === true) {
		return (
			<MemberAvatar
				dataUrl={
					conversation.assignedAvatarKind === 'uploaded'
						? conversation.assignedAvatarDataUrl
						: undefined
				}
				displayName={name}
				enabled
				preset={conversation.assignedAvatarPreset ?? 'anonymous'}
				size={size}
			/>
		);
	}
	return (
		<span aria-hidden="true" className={`conversation-agent-initial member-avatar-${size}`}>
			{name.slice(0, 1).toUpperCase()}
		</span>
	);
}

export function canViewAiChatRatingReview(
	role: MembershipSummary['role'],
	isPlatformAdmin = false,
): boolean {
	return isPlatformAdmin || ['owner', 'admin', 'supervisor'].includes(role);
}

export function AiChatRatingReviewCard({ review }: { review: AiChatRatingReview }) {
	return (
		<section aria-label="AI Review" className="visitor-ai-review-card">
			<div className="visitor-ai-review-heading">
				<strong>AI Review</strong>
				<span>{review.rating} / 5</span>
			</div>
			<p>{review.review}</p>
		</section>
	);
}

export function TenantInbox({
	claimInitialConversation,
	currentUserId,
	initialConversationId,
	isPlatformAdmin,
	membership,
	onActiveConversationChanged,
	onConversationSelected,
	onViewVisitorHistory,
}: {
	claimInitialConversation?: boolean;
	currentUserId: string;
	initialConversationId?: string;
	isPlatformAdmin: boolean;
	membership: MembershipSummary;
	onActiveConversationChanged: (conversationId?: string) => void;
	onConversationSelected: (conversationId?: string) => void;
	onViewVisitorHistory: (visitorId: string) => void;
}) {
	const { formatDateTime, formatTime } = useDateFormat();
	const filter: InboxFilter = 'all';
	const [currentChatScope, setCurrentChatScope] = useState<CurrentChatScope>(() =>
		storedCurrentChatScope(membership.tenantId, currentUserId),
	);
	const [conversations, setConversations] = useState<ConversationSummary[]>([]);
	const [selectedId, setSelectedId] = useState<string>();
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [reply, setReply] = useState('');
	const [replyDraftContext, setReplyDraftContext] = useState<ReplyDraftContext>();
	const [cannedResponses, setCannedResponses] = useState<CannedResponseSummary[]>([]);
	const [cannedOpen, setCannedOpen] = useState(false);
	const [emojiOpen, setEmojiOpen] = useState(false);
	const [chatMenuOpen, setChatMenuOpen] = useState(false);
	const [transferMenuOpen, setTransferMenuOpen] = useState(false);
	const [transferOptions, setTransferOptions] = useState<ConversationTransferOptions>();
	const [transferOptionsLoading, setTransferOptionsLoading] = useState(false);
	const [conversationAction, setConversationAction] = useState<ConversationAction>();
	const [conversationActionBusy, setConversationActionBusy] = useState(false);
	const [conversationActionError, setConversationActionError] = useState<string>();
	const [conversationActionNotice, setConversationActionNotice] = useState<string>();
	const [transcriptEmail, setTranscriptEmail] = useState('');
	const [pushUrl, setPushUrl] = useState('');
	const [cannedQuery, setCannedQuery] = useState('');
	const [cannedIndex, setCannedIndex] = useState(0);
	const [slashTrigger, setSlashTrigger] = useState<SlashTrigger>();
	const [error, setError] = useState<string>();
	const [busy, setBusy] = useState(false);
	const [sending, setSending] = useState(false);
	const [copilotAvailable, setCopilotAvailable] = useState(false);
	const [copilotError, setCopilotError] = useState<string>();
	const [copilotFeedbackBusy, setCopilotFeedbackBusy] = useState(false);
	const [copilotFeedbackRunId, setCopilotFeedbackRunId] = useState<string>();
	const [copilotLoading, setCopilotLoading] = useState(false);
	const [copilotLoadingMode, setCopilotLoadingMode] = useState<AiCopilotMode>();
	const [copilotQuestion, setCopilotQuestion] = useState('');
	const [copilotSuggestion, setCopilotSuggestion] = useState<AiCopilotResponse>();
	const [routingAnalysis, setRoutingAnalysis] = useState<AiRoutingAnalysis>();
	const [routingCorrectionDepartmentId, setRoutingCorrectionDepartmentId] = useState('');
	const [routingCorrectionPriority, setRoutingCorrectionPriority] = useState<
		'' | AiRoutingAnalysis['suggestedPriority']
	>('');
	const [routingCorrectionOpen, setRoutingCorrectionOpen] = useState(false);
	const [routingError, setRoutingError] = useState<string>();
	const [routingFeedbackBusy, setRoutingFeedbackBusy] = useState(false);
	const [routingLoading, setRoutingLoading] = useState(false);
	const [usedCopilotSuggestion, setUsedCopilotSuggestion] = useState<UsedCopilotSuggestion>();
	const [connected, setConnected] = useState(false);
	const [visitorTyping, setVisitorTyping] = useState<TypingPayload>();
	const [visitor, setVisitor] = useState<VisitorSummary>();
	const [visitorLoading, setVisitorLoading] = useState(false);
	const [visitorError, setVisitorError] = useState<string>();
	const [aiRatingReview, setAiRatingReview] = useState<AiChatRatingReview | null>();
	const [queueClock, setQueueClock] = useState(() => Date.now());
	const conversationRefreshRunner = useRef(createRefreshRunner()).current;
	const realtimeConversationVersionRef = useRef(0);
	const realtimeConversationUpdatesRef = useRef(
		new Map<
			string,
			{ allowInsert: boolean; conversation: ConversationSummary; version: number }
		>(),
	);
	const socketRef = useRef<Socket | undefined>(undefined);
	const typingTimerRef = useRef<number | undefined>(undefined);
	const copilotRequestRef = useRef(0);
	const routingRequestRef = useRef(0);
	const selectedIdRef = useRef<string | undefined>(undefined);
	const filterRef = useRef<InboxFilter>('all');
	const composerRef = useRef<HTMLDivElement>(null);
	const conversationMenuRef = useRef<HTMLDivElement>(null);
	const transferMenuRef = useRef<HTMLDivElement>(null);
	const messageThreadRef = useRef<HTMLDivElement>(null);
	const messageThreadPinnedRef = useRef(true);
	const replyRef = useRef<HTMLTextAreaElement>(null);
	const selectionRef = useRef({ end: 0, start: 0 });
	const reconcileConversations = useCallback(
		() =>
			conversationRefreshRunner.run(async () => {
				const refreshVersion = realtimeConversationVersionRef.current;
				const items = await listConversations(membership.tenantId);
				const appliedVersion = realtimeConversationVersionRef.current;
				const realtimeUpdates = [...realtimeConversationUpdatesRef.current.values()].filter(
					(update) => update.version > refreshVersion,
				);
				for (const [conversationId, update] of realtimeConversationUpdatesRef.current) {
					if (update.version <= appliedVersion) {
						realtimeConversationUpdatesRef.current.delete(conversationId);
					}
				}
				setConversations((current) => {
					let reconciledItems = items;
					for (const update of realtimeUpdates) {
						if (
							update.allowInsert ||
							reconciledItems.some((item) => item.id === update.conversation.id) ||
							current.some((item) => item.id === update.conversation.id)
						) {
							reconciledItems = mergeConversation(
								reconciledItems,
								update.conversation,
								'all',
							);
						}
					}
					const deepLinked = current.find(
						(conversation) => conversation.id === initialConversationId,
					);
					return deepLinked && !reconciledItems.some((item) => item.id === deepLinked.id)
						? [deepLinked, ...reconciledItems]
						: reconciledItems;
				});
				setSelectedId((current) =>
					current === initialConversationId ||
					items.some((item) => item.id === current) ||
					realtimeUpdates.some((update) => update.conversation.id === current)
						? current
						: undefined,
				);
			}),
		[conversationRefreshRunner, initialConversationId, membership.tenantId],
	);

	const selected = useMemo(
		() => conversations.find((conversation) => conversation.id === selectedId),
		[conversations, selectedId],
	);
	const matchingCannedResponses = useMemo(
		() => filterCannedResponses(cannedResponses, cannedQuery),
		[cannedQuery, cannedResponses],
	);
	const currentChats = useMemo(
		() =>
			conversations.filter(
				(conversation) =>
					(conversation.status === 'active' || conversation.status === 'waiting') &&
					(currentChatScope === 'all' || conversation.assignedUserId === currentUserId),
			),
		[conversations, currentChatScope, currentUserId],
	);
	const waitingChats = useMemo(() => sortedWaitingChats(conversations), [conversations]);
	const appearanceStyle = useMemo(
		() => bubbleVariables(selected?.widgetBubbleAppearance),
		[selected?.widgetBubbleAppearance],
	);
	const visitorEmail = selected?.visitorEmail || visitor?.email;
	const selectedVisitorCompany = visitor
		? visitorCompanyLabel(visitor)
		: selected
			? visitorCompanyLabel({
					company: selected.visitorCompany,
					name: selected.visitorName,
				})
			: undefined;
	const canReply = Boolean(
		selected &&
		selected.status !== 'closed' &&
		(!selected.assignedUserId || selected.assignedUserId === currentUserId),
	);
	const canWhisper = Boolean(
		selected?.allowWhispers &&
		selected.status !== 'closed' &&
		selected.assignedMemberType === 'human' &&
		selected.assignedUserId &&
		selected.assignedUserId !== currentUserId &&
		['owner', 'admin', 'supervisor'].includes(membership.role),
	);
	const canCompose = canReply || canWhisper;
	const replyDeliveryMode = canWhisper ? 'whisper' : canReply ? 'reply' : undefined;
	const visitorCanReceivePage = Boolean(
		selected && selected.status !== 'closed' && visitor && visitor.status !== 'offline',
	);
	const canPushVisitor = Boolean(
		visitorCanReceivePage && selected?.assignedUserId === currentUserId,
	);
	const hasVisitorDetails = Boolean(
		selectedVisitorCompany ||
		visitorEmail ||
		visitor?.phone ||
		visitor?.notes ||
		visitor?.variables.length,
	);

	useEffect(() => {
		if (selected?.status === 'closed') setVisitorTyping(undefined);
	}, [selected?.status]);

	useEffect(() => {
		if (!reply || replyDraftCanSend(replyDraftContext, selectedId, replyDeliveryMode)) {
			return;
		}
		setReply('');
		setReplyDraftContext(undefined);
		setUsedCopilotSuggestion(undefined);
		setCannedOpen(false);
		setEmojiOpen(false);
		setError('The draft was cleared because this chat’s reply mode changed.');
	}, [reply, replyDeliveryMode, replyDraftContext, selectedId]);

	useEffect(() => {
		if (selectedId || !waitingChats.length) return;
		setQueueClock(Date.now());
		const interval = window.setInterval(() => setQueueClock(Date.now()), 30_000);
		return () => window.clearInterval(interval);
	}, [selectedId, waitingChats.length]);

	useEffect(() => {
		setCurrentChatScope(storedCurrentChatScope(membership.tenantId, currentUserId));
	}, [currentUserId, membership.tenantId]);

	function changeCurrentChatScope(scope: CurrentChatScope): void {
		setCurrentChatScope(scope);
		if (typeof window === 'undefined') return;
		try {
			window.localStorage.setItem(
				currentChatScopeStorageKey(membership.tenantId, currentUserId),
				scope,
			);
		} catch {
			// The preference remains active for this session if storage is unavailable.
		}
	}

	useEffect(() => {
		void listCannedResponses(membership.tenantId)
			.then(setCannedResponses)
			.catch((caught: unknown) =>
				setError(
					caught instanceof Error ? caught.message : 'Could not load canned responses',
				),
			);
	}, [membership.tenantId]);

	useEffect(() => {
		let current = true;
		void getMemberProfile(membership.tenantId, currentUserId)
			.then((profile) => {
				if (current) {
					setCopilotAvailable(profile.aiCopilotAvailable && profile.aiCopilotEnabled);
				}
			})
			.catch(() => {
				if (current) setCopilotAvailable(false);
			});
		return () => {
			current = false;
		};
	}, [currentUserId, membership.tenantId]);

	useEffect(() => {
		function outside(event: PointerEvent): void {
			if (!composerRef.current?.contains(event.target as Node)) {
				closeCannedResponses();
				setEmojiOpen(false);
			}
			if (!conversationMenuRef.current?.contains(event.target as Node)) {
				setChatMenuOpen(false);
			}
			if (!transferMenuRef.current?.contains(event.target as Node)) {
				setTransferMenuOpen(false);
			}
		}
		document.addEventListener('pointerdown', outside);
		return () => document.removeEventListener('pointerdown', outside);
	}, []);

	useEffect(() => {
		const previousId = selectedIdRef.current;
		if (previousId !== selectedId) messageThreadPinnedRef.current = true;
		if (previousId && previousId !== selectedId) {
			window.clearTimeout(typingTimerRef.current);
			socketRef.current?.emit('typing', {
				conversationId: previousId,
				isTyping: false,
			});
		}
		selectedIdRef.current = selectedId;
		copilotRequestRef.current += 1;
		routingRequestRef.current += 1;
		setCopilotError(undefined);
		setCopilotFeedbackBusy(false);
		setCopilotFeedbackRunId(undefined);
		setCopilotLoading(false);
		setCopilotLoadingMode(undefined);
		setCopilotQuestion('');
		setCopilotSuggestion(undefined);
		setRoutingAnalysis(undefined);
		setRoutingCorrectionDepartmentId('');
		setRoutingCorrectionPriority('');
		setRoutingCorrectionOpen(false);
		setRoutingError(undefined);
		setRoutingFeedbackBusy(false);
		setRoutingLoading(false);
		setChatMenuOpen(false);
		setTransferMenuOpen(false);
		setTransferOptions(undefined);
		setConversationAction(undefined);
		setConversationActionError(undefined);
		setConversationActionNotice(undefined);
		setEmojiOpen(false);
		onActiveConversationChanged(selectedId);
	}, [onActiveConversationChanged, selectedId]);

	useEffect(() => {
		setSelectedId(initialConversationId);
		if (!initialConversationId) return;
		const open = claimInitialConversation
			? assignConversation(membership.tenantId, initialConversationId)
			: getConversation(membership.tenantId, initialConversationId);
		void open
			.then((conversation) =>
				setConversations((current) => mergeConversation(current, conversation, 'all')),
			)
			.catch((caught: unknown) =>
				setError(
					caught instanceof Error ? caught.message : 'Could not open the conversation',
				),
			);
	}, [claimInitialConversation, initialConversationId, membership.tenantId]);

	useEffect(() => {
		setError(undefined);
		void reconcileConversations().catch((caught: unknown) =>
			setError(caught instanceof Error ? caught.message : 'Could not load conversations'),
		);
	}, [reconcileConversations]);

	useEffect(() => {
		const token = currentAccessToken();
		if (!token) {
			return;
		}
		const socket = io(`${realtimeUrl}/chat`, {
			auth: { accessToken: token, tenantId: membership.tenantId },
			transports: ['websocket', 'polling'],
		});
		let lastRealtimeRefreshAt = 0;
		const refreshQueue = () => {
			void reconcileConversations().catch((caught: unknown) =>
				setError(
					caught instanceof Error ? caught.message : 'Could not refresh conversations',
				),
			);
		};
		const applyRealtimeConversation = (
			conversation: ConversationSummary,
			allowInsert: boolean,
		) => {
			const version = ++realtimeConversationVersionRef.current;
			realtimeConversationUpdatesRef.current.set(conversation.id, {
				allowInsert,
				conversation,
				version,
			});
			setConversations((current) => {
				if (!allowInsert && !current.some((item) => item.id === conversation.id)) {
					return current;
				}
				return mergeConversation(current, conversation, filterRef.current);
			});
		};
		socketRef.current = socket;
		const stopWatchingToken = onAccessTokenChanged((updatedToken) => {
			socket.auth = { accessToken: updatedToken, tenantId: membership.tenantId };
			if (updatedToken && !socket.connected) socket.connect();
		});
		socket.on('connect', () => {
			setConnected(true);
			refreshQueue();
			const conversationId = selectedIdRef.current;
			if (conversationId) socket.emit('conversation:join', { conversationId });
		});
		socket.on('disconnect', () => setConnected(false));
		socket.on('session:error', () => {
			if (Date.now() - lastRealtimeRefreshAt < 60_000) return;
			lastRealtimeRefreshAt = Date.now();
			void refreshSession()
				.then((authentication) => {
					socket.auth = {
						accessToken: authentication.accessToken,
						tenantId: membership.tenantId,
					};
					socket.connect();
				})
				.catch((caught: unknown) =>
					setError(
						caught instanceof Error ? caught.message : 'Realtime connection failed',
					),
				);
		});
		socket.on('conversation:created', (conversation: ConversationSummary) => {
			applyRealtimeConversation(conversation, true);
		});
		socket.on('queue:changed', refreshQueue);
		socket.on('conversation:updated', (conversation: ConversationSummary) => {
			applyRealtimeConversation(conversation, conversation.status !== 'queued');
			if (conversation.status === 'queued') {
				refreshQueue();
				return;
			}
		});
		socket.on('message:created', (message: ChatMessage) => {
			if (message.conversationId === selectedIdRef.current) {
				setMessages((current) => mergeMessage(current, message));
				setRoutingAnalysis((current) =>
					current &&
					message.kind !== 'internal_note' &&
					message.sequence > current.messageSequence
						? { ...current, stale: true }
						: current,
				);
				if (message.senderType === 'visitor') setVisitorTyping(undefined);
			}
		});
		socket.on('routing:analysed', (payload: { conversationId: string }) => {
			if (payload.conversationId !== selectedIdRef.current) return;
			void getLatestConversationRoutingAnalysis(membership.tenantId, payload.conversationId)
				.then((analysis) => setRoutingAnalysis(analysis ?? undefined))
				.catch(() => undefined);
		});
		socket.on('typing', (payload: TypingPayload) => {
			if (
				payload.senderType === 'visitor' &&
				payload.conversationId === selectedIdRef.current
			) {
				setVisitorTyping(payload.isTyping ? payload : undefined);
			}
		});
		return () => {
			stopWatchingToken();
			window.clearTimeout(typingTimerRef.current);
			socket.disconnect();
			socketRef.current = undefined;
		};
	}, [membership.tenantId, reconcileConversations]);

	useEffect(() => {
		closeCannedResponses();
		setVisitorTyping(undefined);
		setMessages([]);
		if (!selectedId) {
			return;
		}
		let current = true;
		void listConversationMessages(membership.tenantId, selectedId)
			.then((loadedMessages) => {
				if (!current || selectedIdRef.current !== selectedId) return;
				setMessages((existingMessages) =>
					loadedMessages.reduce(
						mergeMessage,
						existingMessages.filter((message) => message.conversationId === selectedId),
					),
				);
			})
			.catch(
				(caught: unknown) =>
					current &&
					setError(caught instanceof Error ? caught.message : 'Could not load messages'),
			);
		socketRef.current?.emit('conversation:join', { conversationId: selectedId });
		return () => {
			current = false;
		};
	}, [membership.tenantId, selectedId]);

	useEffect(() => {
		if (
			!copilotAvailable ||
			!selected ||
			!['active', 'waiting'].includes(selected.status) ||
			selected.assignedUserId !== currentUserId
		) {
			return;
		}
		let current = true;
		void getLatestConversationRoutingAnalysis(membership.tenantId, selected.id)
			.then((analysis) => {
				if (current) setRoutingAnalysis(analysis ?? undefined);
			})
			.catch(() => undefined);
		return () => {
			current = false;
		};
	}, [copilotAvailable, currentUserId, membership.tenantId, selected]);

	useEffect(() => {
		if (!selected?.visitorId) {
			setVisitor(undefined);
			setVisitorError(undefined);
			return;
		}
		let current = true;
		setVisitor(undefined);
		setVisitorLoading(true);
		setVisitorError(undefined);
		void getVisitor(membership.tenantId, selected.visitorId)
			.then((loadedVisitor) => {
				if (current) setVisitor(loadedVisitor);
			})
			.catch((caught: unknown) => {
				if (current) {
					setVisitorError(
						caught instanceof Error ? caught.message : 'Could not load visitor details',
					);
				}
			})
			.finally(() => {
				if (current) setVisitorLoading(false);
			});
		return () => {
			current = false;
		};
	}, [membership.tenantId, selected?.visitorId]);

	useEffect(() => {
		setAiRatingReview(undefined);
		if (
			!selected ||
			selected.status !== 'closed' ||
			!canViewAiChatRatingReview(membership.role, isPlatformAdmin)
		) {
			return;
		}
		let current = true;
		void getConversationAiRatingReview(membership.tenantId, selected.id)
			.then((review) => {
				if (current && selectedIdRef.current === selected.id) setAiRatingReview(review);
			})
			.catch(() => {
				if (current) setAiRatingReview(null);
			});
		return () => {
			current = false;
		};
	}, [isPlatformAdmin, membership.role, membership.tenantId, selected]);

	useEffect(() => {
		const thread = messageThreadRef.current;
		if (!thread || !messageThreadPinnedRef.current) return;
		thread.scrollTo({ behavior: 'smooth', top: thread.scrollHeight });
	}, [messages, visitorTyping]);

	async function send(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (
			!selectedId ||
			!reply.trim() ||
			!canCompose ||
			!replyDraftCanSend(replyDraftContext, selectedId, replyDeliveryMode)
		) {
			return;
		}
		setBusy(true);
		setSending(true);
		setError(undefined);
		const conversationId = selectedId;
		const sentCopilotSuggestion = usedCopilotSuggestion;
		try {
			emitAgentTyping(false);
			const sentBody = reply.trim();
			const message = await sendConversationMessage(
				membership.tenantId,
				conversationId,
				sentBody,
				replyDraftContext?.mode === 'whisper',
			);
			if (selectedIdRef.current === conversationId) {
				setMessages((current) =>
					mergeMessage(
						current.filter((item) => item.conversationId === conversationId),
						message,
					),
				);
			}
			if (sentCopilotSuggestion?.conversationId === conversationId) {
				const editDistance = copilotEditDistance(sentCopilotSuggestion.message, sentBody);
				void recordConversationCopilotFeedback(
					membership.tenantId,
					conversationId,
					sentCopilotSuggestion.runId,
					editDistance ? { editDistance, outcome: 'edited' } : { outcome: 'accepted' },
				).catch(() => undefined);
			}
			if (selectedIdRef.current !== conversationId) return;
			setUsedCopilotSuggestion(undefined);
			copilotRequestRef.current += 1;
			setCopilotLoading(false);
			setCopilotLoadingMode(undefined);
			setCopilotError(undefined);
			setCopilotFeedbackBusy(false);
			setCopilotFeedbackRunId(undefined);
			setCopilotSuggestion(undefined);
			setReply('');
			setReplyDraftContext(undefined);
			closeCannedResponses();
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not send the reply');
		} finally {
			setSending(false);
			setBusy(false);
		}
	}

	async function requestCopilot(mode: AiCopilotMode): Promise<void> {
		if (
			!selected ||
			!['active', 'waiting'].includes(selected.status) ||
			selected.assignedUserId !== currentUserId ||
			copilotLoading ||
			(mode === 'ask_ai' && !copilotQuestion.trim())
		) {
			return;
		}
		const conversationId = selected.id;
		const requestId = copilotRequestRef.current + 1;
		copilotRequestRef.current = requestId;
		setCopilotLoading(true);
		setCopilotLoadingMode(mode);
		setCopilotError(undefined);
		setCopilotFeedbackRunId(undefined);
		try {
			const suggestion =
				mode === 'suggest_reply'
					? await suggestConversationReply(membership.tenantId, conversationId)
					: mode === 'ask_ai'
						? await askConversationAi(membership.tenantId, conversationId, {
								question: copilotQuestion.trim(),
							})
						: mode === 'conversation_summary'
							? await summarizeConversation(membership.tenantId, conversationId)
							: await helpWithConversationAnswer(membership.tenantId, conversationId);
			if (
				copilotRequestRef.current === requestId &&
				selectedIdRef.current === conversationId
			) {
				setCopilotSuggestion(suggestion);
				if (mode === 'ask_ai') setCopilotQuestion('');
			}
		} catch (caught) {
			if (copilotRequestRef.current === requestId) {
				setCopilotError(
					caught instanceof Error
						? caught.message
						: 'Copilot could not provide assistance',
				);
			}
		} finally {
			if (copilotRequestRef.current === requestId) {
				setCopilotLoading(false);
				setCopilotLoadingMode(undefined);
			}
		}
	}

	async function analyseRouting(): Promise<void> {
		if (
			!selected ||
			!['active', 'waiting'].includes(selected.status) ||
			selected.assignedUserId !== currentUserId ||
			routingLoading
		) {
			return;
		}
		const conversationId = selected.id;
		const requestId = routingRequestRef.current + 1;
		routingRequestRef.current = requestId;
		setRoutingLoading(true);
		setRoutingError(undefined);
		setRoutingCorrectionOpen(false);
		setRoutingCorrectionDepartmentId('');
		setRoutingCorrectionPriority('');
		try {
			const analysis = await analyseConversationRouting(membership.tenantId, conversationId);
			if (
				routingRequestRef.current === requestId &&
				selectedIdRef.current === conversationId
			) {
				setRoutingAnalysis(analysis);
			}
		} catch (caught) {
			if (routingRequestRef.current === requestId) {
				setRoutingError(
					caught instanceof Error
						? caught.message
						: 'Routing analysis could not be completed',
				);
			}
		} finally {
			if (routingRequestRef.current === requestId) setRoutingLoading(false);
		}
	}

	async function submitRoutingFeedback(
		outcome: 'accepted' | 'corrected' | 'dismissed',
	): Promise<void> {
		if (
			!selected ||
			!routingAnalysis ||
			routingAnalysis.feedbackOutcome ||
			routingFeedbackBusy ||
			(outcome === 'corrected' &&
				!routingCorrectionDepartmentId &&
				!routingCorrectionPriority)
		) {
			return;
		}
		setRoutingFeedbackBusy(true);
		setRoutingError(undefined);
		try {
			await recordConversationRoutingFeedback(
				membership.tenantId,
				selected.id,
				routingAnalysis.id,
				outcome === 'corrected'
					? {
							...(routingCorrectionDepartmentId
								? { departmentId: routingCorrectionDepartmentId }
								: {}),
							outcome,
							...(routingCorrectionPriority
								? { priority: routingCorrectionPriority }
								: {}),
						}
					: { outcome },
			);
			setRoutingAnalysis((current) =>
				current?.id === routingAnalysis.id
					? {
							...current,
							feedbackOutcome: outcome,
							...(outcome === 'corrected' && routingCorrectionPriority
								? { feedbackPriority: routingCorrectionPriority }
								: {}),
						}
					: current,
			);
			setRoutingCorrectionOpen(false);
			setRoutingCorrectionPriority('');
		} catch (caught) {
			setRoutingError(
				caught instanceof Error ? caught.message : 'Could not record routing feedback',
			);
		} finally {
			setRoutingFeedbackBusy(false);
		}
	}

	function useCopilotSuggestion(): void {
		if (
			!selected ||
			!copilotSuggestion ||
			copilotSuggestion.mode !== 'suggest_reply' ||
			reply.trim()
		) {
			return;
		}
		setReply(copilotSuggestion.message);
		setReplyDraftContext({ conversationId: selected.id, mode: 'reply' });
		setUsedCopilotSuggestion({
			conversationId: selected.id,
			message: copilotSuggestion.message,
			runId: copilotSuggestion.runId,
		});
		emitAgentTyping(true);
		requestAnimationFrame(() => {
			replyRef.current?.focus();
			replyRef.current?.setSelectionRange(
				copilotSuggestion.message.length,
				copilotSuggestion.message.length,
			);
		});
	}

	function dismissCopilotSuggestion(): void {
		if (
			!selected ||
			!copilotSuggestion ||
			usedCopilotSuggestion?.runId === copilotSuggestion.runId
		) {
			return;
		}
		if (copilotSuggestion.mode === 'suggest_reply') {
			void recordConversationCopilotFeedback(
				membership.tenantId,
				selected.id,
				copilotSuggestion.runId,
				{ outcome: 'rejected' },
			).catch(() => undefined);
		}
		setCopilotSuggestion(undefined);
		setCopilotFeedbackRunId(undefined);
		setCopilotError(undefined);
	}

	async function recordCopilotGuidanceFeedback(outcome: 'helpful' | 'unhelpful'): Promise<void> {
		if (
			!selected ||
			!copilotSuggestion ||
			copilotSuggestion.mode === 'suggest_reply' ||
			copilotFeedbackBusy ||
			copilotFeedbackRunId === copilotSuggestion.runId
		) {
			return;
		}
		const conversationId = selected.id;
		const runId = copilotSuggestion.runId;
		setCopilotFeedbackBusy(true);
		setCopilotError(undefined);
		try {
			await recordConversationCopilotFeedback(membership.tenantId, conversationId, runId, {
				outcome,
			});
			if (selectedIdRef.current === conversationId) setCopilotFeedbackRunId(runId);
		} catch (caught) {
			setCopilotError(
				caught instanceof Error ? caught.message : 'Could not record Copilot feedback',
			);
		} finally {
			setCopilotFeedbackBusy(false);
		}
	}

	function closeCannedResponses(): void {
		setCannedOpen(false);
		setCannedQuery('');
		setCannedIndex(0);
		setSlashTrigger(undefined);
	}

	function rememberSelection(): void {
		const input = replyRef.current;
		if (!input) return;
		selectionRef.current = {
			end: input.selectionEnd ?? input.value.length,
			start: input.selectionStart ?? input.value.length,
		};
	}

	function changeReply(value: string, cursor: number): void {
		const suggestion = usedCopilotSuggestion;
		if (!value && suggestion && suggestion.conversationId === selectedIdRef.current) {
			void recordConversationCopilotFeedback(
				membership.tenantId,
				suggestion.conversationId,
				suggestion.runId,
				{ outcome: 'rejected' },
			).catch(() => undefined);
			setUsedCopilotSuggestion(undefined);
			setCopilotSuggestion(undefined);
		}
		if (!value) {
			setReplyDraftContext(undefined);
		} else if (!replyDraftContext && selectedIdRef.current && replyDeliveryMode) {
			setReplyDraftContext({
				conversationId: selectedIdRef.current,
				mode: replyDeliveryMode,
			});
		}
		setReply(value);
		emitAgentTyping(Boolean(value));
		selectionRef.current = { end: cursor, start: cursor };
		const trigger = findSlashTrigger(value, cursor);
		if (trigger) {
			setSlashTrigger(trigger);
			setCannedQuery(trigger.query);
			setCannedIndex(0);
			setCannedOpen(true);
		} else if (slashTrigger) {
			closeCannedResponses();
		}
	}

	function emitAgentTyping(isTyping: boolean): void {
		if (canWhisper) return;
		const conversationId = selectedIdRef.current;
		if (!conversationId) return;
		window.clearTimeout(typingTimerRef.current);
		socketRef.current?.emit('typing', { conversationId, isTyping });
		if (isTyping) {
			typingTimerRef.current = window.setTimeout(
				() => socketRef.current?.emit('typing', { conversationId, isTyping: false }),
				900,
			);
		}
	}

	function toggleCannedResponses(): void {
		if (cannedOpen) {
			closeCannedResponses();
			requestAnimationFrame(() => replyRef.current?.focus());
			return;
		}
		rememberSelection();
		setEmojiOpen(false);
		setSlashTrigger(undefined);
		setCannedQuery('');
		setCannedIndex(0);
		setCannedOpen(true);
	}

	function chooseEmoji(emoji: string): void {
		const inserted = insertEmoji(
			reply,
			emoji,
			selectionRef.current.start,
			selectionRef.current.end,
		);
		setReply(inserted.value);
		if (!replyDraftContext && selectedIdRef.current && replyDeliveryMode) {
			setReplyDraftContext({
				conversationId: selectedIdRef.current,
				mode: replyDeliveryMode,
			});
		}
		emitAgentTyping(true);
		setEmojiOpen(false);
		requestAnimationFrame(() => {
			replyRef.current?.focus();
			replyRef.current?.setSelectionRange(inserted.cursor, inserted.cursor);
			selectionRef.current = { end: inserted.cursor, start: inserted.cursor };
		});
	}

	function openConversationAction(action: ConversationAction): void {
		setChatMenuOpen(false);
		setConversationActionError(undefined);
		setConversationAction(action);
		if (action === 'email') setTranscriptEmail(visitorEmail ?? '');
		if (action === 'push') setPushUrl(visitor?.pageUrl ?? '');
	}

	async function closeSelectedConversation(): Promise<void> {
		if (!selected || busy) return;
		setBusy(true);
		setError(undefined);
		try {
			const closed = await closeConversation(membership.tenantId, selected.id);
			setConversations((current) => mergeConversation(current, closed, filter));
			setConversationActionNotice('The chat was closed.');
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not close the chat');
		} finally {
			setBusy(false);
		}
	}

	async function confirmBlockVisitor(): Promise<void> {
		if (!selected) return;
		setConversationActionBusy(true);
		setConversationActionError(undefined);
		try {
			const result = await blockConversationVisitor(membership.tenantId, selected.id);
			setConversations((current) => mergeConversation(current, result.conversation, filter));
			setConversationAction(undefined);
			setConversationActionNotice(`${selected.visitorName ?? 'Visitor'} has been blocked.`);
		} catch (caught) {
			setConversationActionError(
				caught instanceof Error ? caught.message : 'Could not block the visitor',
			);
		} finally {
			setConversationActionBusy(false);
		}
	}

	async function emailConversation(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (!selected || !transcriptEmail.trim()) return;
		setConversationActionBusy(true);
		setConversationActionError(undefined);
		try {
			await emailConversationTranscript(
				membership.tenantId,
				selected.id,
				transcriptEmail.trim(),
			);
			setConversationAction(undefined);
			setConversationActionNotice(`Conversation queued for ${transcriptEmail.trim()}.`);
		} catch (caught) {
			setConversationActionError(
				caught instanceof Error ? caught.message : 'Could not email the conversation',
			);
		} finally {
			setConversationActionBusy(false);
		}
	}

	async function pushVisitor(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (!selected || !pushUrl.trim()) return;
		setConversationActionBusy(true);
		setConversationActionError(undefined);
		try {
			await pushConversationVisitor(membership.tenantId, selected.id, pushUrl.trim());
			setConversationAction(undefined);
			setConversationActionNotice('The visitor was sent to the requested page.');
		} catch (caught) {
			setConversationActionError(
				caught instanceof Error ? caught.message : 'Could not push the visitor',
			);
		} finally {
			setConversationActionBusy(false);
		}
	}

	function selectCannedResponse(response: CannedResponseSummary): void {
		const inserted = insertCannedResponse(
			reply,
			response.body,
			selectionRef.current.start,
			selectionRef.current.end,
			slashTrigger,
		);
		setReply(inserted.value);
		if (!replyDraftContext && selectedIdRef.current && replyDeliveryMode) {
			setReplyDraftContext({
				conversationId: selectedIdRef.current,
				mode: replyDeliveryMode,
			});
		}
		emitAgentTyping(Boolean(inserted.value));
		closeCannedResponses();
		requestAnimationFrame(() => {
			replyRef.current?.focus();
			replyRef.current?.setSelectionRange(inserted.cursor, inserted.cursor);
			selectionRef.current = { end: inserted.cursor, start: inserted.cursor };
		});
	}

	function replyKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
		if (event.key === 'Enter' && event.shiftKey) return;
		if (cannedOpen && event.key === 'Escape') {
			event.preventDefault();
			closeCannedResponses();
		} else if (cannedOpen && event.key === 'ArrowDown') {
			event.preventDefault();
			setCannedIndex((current) =>
				matchingCannedResponses.length ? (current + 1) % matchingCannedResponses.length : 0,
			);
		} else if (cannedOpen && event.key === 'ArrowUp') {
			event.preventDefault();
			setCannedIndex((current) =>
				matchingCannedResponses.length
					? (current - 1 + matchingCannedResponses.length) %
						matchingCannedResponses.length
					: 0,
			);
		} else if (cannedOpen && isCannedSelectionKey(event.key)) {
			event.preventDefault();
			const response = matchingCannedResponses[cannedIndex];
			if (response) selectCannedResponse(response);
		} else if (
			shouldSubmitReply({
				cannedResponsesOpen: cannedOpen,
				isComposing: event.nativeEvent.isComposing,
				key: event.key,
				shiftKey: event.shiftKey,
			})
		) {
			event.preventDefault();
			if (!busy && canCompose && reply.trim()) {
				event.currentTarget.form?.requestSubmit();
			}
		}
	}

	async function changeSelected(action: () => Promise<ConversationSummary>): Promise<void> {
		setBusy(true);
		setError(undefined);
		try {
			const changed = await action();
			setConversations((current) => mergeConversation(current, changed, filter));
		} catch (caught) {
			setError(
				caught instanceof Error ? caught.message : 'Could not update the conversation',
			);
		} finally {
			setBusy(false);
		}
	}

	function openConversation(conversationId: string): void {
		emitAgentTyping(false);
		setSelectedId(conversationId);
		onConversationSelected(conversationId);
	}

	async function acceptConversation(conversationId: string): Promise<void> {
		setBusy(true);
		setError(undefined);
		try {
			const accepted = await assignConversation(membership.tenantId, conversationId);
			setConversations((current) => mergeConversation(current, accepted, filter));
			openConversation(accepted.id);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not accept the chat');
			try {
				await reconcileConversations();
			} catch {
				// Keep the original claim error; socket reconnect will retry reconciliation.
			}
		} finally {
			setBusy(false);
		}
	}

	async function openTransferMenu(conversationId: string): Promise<void> {
		if (transferMenuOpen) {
			setTransferMenuOpen(false);
			return;
		}
		setTransferMenuOpen(true);
		setTransferOptions(undefined);
		setTransferOptionsLoading(true);
		setError(undefined);
		try {
			setTransferOptions(
				await getConversationTransferOptions(membership.tenantId, conversationId),
			);
		} catch (caught) {
			setTransferMenuOpen(false);
			setError(caught instanceof Error ? caught.message : 'Could not load transfer options');
		} finally {
			setTransferOptionsLoading(false);
		}
	}

	async function transferSelected(
		conversationId: string,
		destination: { departmentId: string } | { userId: string },
	): Promise<void> {
		setBusy(true);
		setError(undefined);
		try {
			const transferred = await transferConversation(
				membership.tenantId,
				conversationId,
				destination,
			);
			setConversations((current) => mergeConversation(current, transferred, filter));
			setTransferMenuOpen(false);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not transfer the chat');
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className={`inbox-wrap ${selected ? 'conversation-workspace' : ''}`}>
			{!selected && (
				<div className="subsection-heading inbox-heading">
					<div>
						<h3>Chats</h3>
						<p>View current conversations or accept a waiting chat.</p>
					</div>
					<span className={`realtime-state ${connected ? 'connected' : ''}`}>
						<span /> {connected ? 'Live' : 'Reconnecting'}
					</span>
				</div>
			)}
			{error && <div className="alert error inbox-alert">{error}</div>}
			{conversationActionNotice && selected && (
				<div className="alert success inbox-alert">{conversationActionNotice}</div>
			)}
			{!selected ? (
				<div className="chat-queue-layout">
					<section className="chat-queue-panel">
						<header className="table-section-heading">
							<div>
								<span className="queue-dot current" />
								<h4>Current chats</h4>
								<span className="queue-count table-section-count">
									{currentChats.length}
								</span>
							</div>
							<label className="visually-hidden" htmlFor="current-chat-scope">
								Current chat view
							</label>
							<select
								className="current-chat-scope"
								id="current-chat-scope"
								onChange={(event) =>
									changeCurrentChatScope(
										event.currentTarget.value as CurrentChatScope,
									)
								}
								value={currentChatScope}
							>
								<option value="mine">My Chats</option>
								<option value="all">All Chats</option>
							</select>
						</header>
						<div
							className="chat-queue-table scrollable-data-table"
							role="table"
							aria-label="Current chats"
						>
							<div
								className="chat-queue-row current-chat-row chat-queue-head standard-table-head"
								role="row"
							>
								<span>Visitor</span>
								<span>Agent</span>
								<span>Department</span>
								<span>Last message</span>
								<span>Updated</span>
							</div>
							{currentChats.map((conversation) => (
								<div
									aria-label={`Open chat with ${conversation.visitorName ?? 'visitor'}`}
									className="chat-queue-row current-chat-row chat-queue-link"
									key={conversation.id}
									onKeyDown={(event) => {
										if (event.target !== event.currentTarget) return;
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault();
											openConversation(conversation.id);
										}
									}}
									onClick={() => openConversation(conversation.id)}
									role="row"
									tabIndex={0}
								>
									<span className="visitor-name-copy">
										<strong>{conversation.visitorName ?? 'Visitor'}</strong>
										{visitorCompanyLabel({
											company: conversation.visitorCompany,
											name: conversation.visitorName,
										}) && (
											<small>
												{visitorCompanyLabel({
													company: conversation.visitorCompany,
													name: conversation.visitorName,
												})}
											</small>
										)}
									</span>
									<span className="current-chat-agent">
										<ConversationAgentAvatar conversation={conversation} />
										<span className="visitor-agent">
											<span>{conversation.assignedUserName ?? '—'}</span>
											{conversation.assignedUserName && (
												<small>
													{conversation.assignedMemberType === 'ai_agent'
														? 'AI Agent'
														: 'Agent'}
												</small>
											)}
										</span>
									</span>
									<span>{conversation.departmentName ?? 'General'}</span>
									<span className="queue-message">
										{conversation.lastMessage ?? 'Conversation started'}
									</span>
									<time>
										{formatTime(
											conversation.lastMessageAt ?? conversation.createdAt,
										)}
									</time>
								</div>
							))}
							{!currentChats.length && (
								<div className="chat-queue-empty">
									{currentChatScope === 'mine'
										? 'You have no current chats.'
										: 'There are no current chats.'}
								</div>
							)}
						</div>
					</section>

					<section className="chat-queue-panel waiting-panel">
						<header className="table-section-heading">
							<div>
								<span className="queue-dot waiting" />
								<h4>Waiting chats</h4>
								<span className="queue-count table-section-count">
									{waitingChats.length}
								</span>
							</div>
						</header>
						<div
							className="chat-queue-table scrollable-data-table"
							role="table"
							aria-label="Waiting chats"
						>
							<div
								className="chat-queue-row chat-queue-head standard-table-head"
								role="row"
							>
								<span>Visitor</span>
								<span>Department</span>
								<span>Message</span>
								<span>Waiting</span>
								<span />
							</div>
							{waitingChats.map((conversation) => (
								<div className="chat-queue-row" role="row" key={conversation.id}>
									<strong>{conversation.visitorName ?? 'Visitor'}</strong>
									<span>{conversation.departmentName ?? 'General'}</span>
									<span className="queue-message">
										{conversation.lastMessage ?? 'New conversation'}
									</span>
									<time dateTime={conversation.createdAt}>
										{waitingDuration(
											conversation.queuedAt ?? conversation.createdAt,
											queueClock,
										)}
									</time>
									<button
										className="primary-button compact-button"
										disabled={busy}
										onClick={() => void acceptConversation(conversation.id)}
									>
										Accept
									</button>
								</div>
							))}
							{!waitingChats.length && (
								<div className="chat-queue-empty">Nobody is waiting right now.</div>
							)}
						</div>
					</section>
				</div>
			) : (
				<div className="conversation-detail-layout" style={appearanceStyle}>
					<div className="inbox-shell conversation-only">
						<section className="conversation-panel">
							<header className="conversation-header">
								<div className="conversation-identity">
									<div className="conversation-identity-copy">
										<div className="conversation-participants">
											<strong>{selected.visitorName ?? 'Visitor'}</strong>
											{selected.assignedUserName && (
												<>
													<span>
														{selected.status === 'closed'
															? 'chatted with'
															: 'chatting with'}
													</span>
													<strong>{selected.assignedUserName}</strong>
												</>
											)}
										</div>
										<span className="conversation-department">
											{selected.departmentName ?? 'General'} department
										</span>
									</div>
								</div>
								<div className="conversation-actions">
									{selected.assignedUserName && (
										<span
											aria-label={`${
												selected.status === 'closed'
													? 'Chatted with'
													: 'Chatting with'
											} ${assignedTeamMemberLabel(
												selected.assignedUserName,
												selected.assignedMemberType,
												'',
											)}`}
											className="conversation-assigned-agent"
											title={`${
												selected.status === 'closed'
													? 'Chatted with'
													: 'Chatting with'
											} ${assignedTeamMemberLabel(
												selected.assignedUserName,
												selected.assignedMemberType,
												'',
											)}`}
										>
											<ConversationAgentAvatar conversation={selected} />
										</span>
									)}
									{selected.status !== 'closed' && !selected.assignedUserId && (
										<button
											disabled={busy}
											onClick={() =>
												void changeSelected(() =>
													assignConversation(
														membership.tenantId,
														selected.id,
													),
												)
											}
										>
											Claim
										</button>
									)}
									{selected.status !== 'closed' &&
										selected.assignedUserId === currentUserId && (
											<div
												className="conversation-transfer"
												ref={transferMenuRef}
											>
												<button
													aria-expanded={transferMenuOpen}
													aria-haspopup="menu"
													className="transfer-selected-chat-button"
													disabled={busy}
													onClick={() =>
														void openTransferMenu(selected.id)
													}
													type="button"
												>
													Transfer Chat
												</button>
												{transferMenuOpen && (
													<div
														className="conversation-transfer-menu"
														role="menu"
													>
														{transferOptionsLoading && (
															<span>Loading…</span>
														)}
														{Boolean(
															transferOptions?.departments.length,
														) && <strong>Online Departments</strong>}
														{transferOptions?.departments.map(
															(department) => (
																<button
																	key={department.id}
																	onClick={() =>
																		void transferSelected(
																			selected.id,
																			{
																				departmentId:
																					department.id,
																			},
																		)
																	}
																	role="menuitem"
																	type="button"
																>
																	{department.name}
																</button>
															),
														)}
														<strong>Online Agents</strong>
														{transferOptions?.agents.map((agent) => (
															<button
																key={agent.id}
																onClick={() =>
																	void transferSelected(
																		selected.id,
																		{
																			userId: agent.id,
																		},
																	)
																}
																role="menuitem"
																type="button"
															>
																{agent.displayName}
															</button>
														))}
														{transferOptions &&
															!transferOptions.agents.length &&
															!transferOptions.departments.length && (
																<span>
																	No other transfer destinations
																	are online.
																</span>
															)}
													</div>
												)}
											</div>
										)}
									{selected.status !== 'closed' &&
										selected.assignedUserId &&
										selected.assignedUserId !== currentUserId && (
											<button
												className="takeover-selected-chat-button"
												disabled={busy}
												onClick={() =>
													void changeSelected(() =>
														assignConversation(
															membership.tenantId,
															selected.id,
															currentUserId,
														),
													)
												}
												type="button"
											>
												Take Over
											</button>
										)}
									{selected.status === 'active' && (
										<button
											className="close-selected-chat-button"
											disabled={busy}
											onClick={() => void closeSelectedConversation()}
											type="button"
										>
											Close Chat
										</button>
									)}
									<div className="conversation-more" ref={conversationMenuRef}>
										<button
											aria-expanded={chatMenuOpen}
											aria-haspopup="menu"
											aria-label="Conversation actions"
											className="conversation-more-button"
											onClick={() => setChatMenuOpen((open) => !open)}
											type="button"
										>
											<svg aria-hidden="true" viewBox="0 0 24 24">
												<circle cx="12" cy="5" r="1.8" />
												<circle cx="12" cy="12" r="1.8" />
												<circle cx="12" cy="19" r="1.8" />
											</svg>
										</button>
										{chatMenuOpen && (
											<div className="conversation-more-menu" role="menu">
												{visitor?.cobrowseEnabled && (
													<button
														disabled={
															visitorLoading ||
															visitor.status === 'offline'
														}
														onClick={() => {
															setChatMenuOpen(false);
															try {
																openCobrowseWindow(
																	membership.tenantId,
																	visitor,
																);
															} catch (caught) {
																setVisitorError(
																	caught instanceof Error
																		? caught.message
																		: 'Could not open Cobrowse',
																);
															}
														}}
														role="menuitem"
														title={
															visitor.status === 'offline'
																? 'Visitor is offline'
																: undefined
														}
														type="button"
													>
														Cobrowse
													</button>
												)}
												<button
													onClick={() => openConversationAction('email')}
													role="menuitem"
													type="button"
												>
													Email Conversation
												</button>
												{visitorCanReceivePage && (
													<button
														disabled={!canPushVisitor}
														onClick={() =>
															openConversationAction('push')
														}
														role="menuitem"
														title={
															canPushVisitor
																? undefined
																: 'Take over this chat before sending the visitor to a page'
														}
														type="button"
													>
														Send To Page
													</button>
												)}
												<button
													className="danger"
													onClick={() => openConversationAction('block')}
													role="menuitem"
													type="button"
												>
													Block Visitor
												</button>
											</div>
										)}
									</div>
								</div>
							</header>
							<div
								className="message-thread"
								onScroll={(event) => {
									messageThreadPinnedRef.current = messageThreadAtBottom(
										event.currentTarget,
									);
								}}
								ref={messageThreadRef}
							>
								{messages.map((message) => (
									<div
										className={`message-row ${message.senderType}${message.kind === 'internal_note' ? ' message-whisper' : ''}${message.originalBody ? ' message-enhanced' : ''}`}
										key={message.id}
									>
										<div className="message-copy">
											<div
												className="message-bubble"
												title={
													message.originalBody
														? `Original message:\n${message.originalBody}`
														: undefined
												}
											>
												<p>{message.body}</p>
											</div>
											<time className="message-time">
												{formatDateTime(message.createdAt)}
											</time>
										</div>
										{message.senderType === 'agent' && (
											<MemberAvatar
												dataUrl={message.senderAvatarDataUrl}
												displayName={message.senderName ?? 'Agent'}
												enabled={message.senderAvatarEnabled === true}
												preset={message.senderAvatarPreset ?? 'anonymous'}
												size="small"
											/>
										)}
									</div>
								))}
								{visitorTyping && selected.status !== 'closed' && (
									<div className="typing-preview" aria-live="polite">
										<strong>Visitor is typing…</strong>
										<p>{visitorTyping.draft || '…'}</p>
									</div>
								)}
							</div>
							<div
								className="reply-composer-wrap"
								hidden={!canCompose}
								ref={composerRef}
							>
								{cannedOpen && (
									<CannedResponseMenu
										activeIndex={cannedIndex}
										onActiveIndexChange={setCannedIndex}
										onClose={() => {
											closeCannedResponses();
											replyRef.current?.focus();
										}}
										onQueryChange={(query) => {
											setCannedQuery(query);
											setCannedIndex(0);
										}}
										onSelect={selectCannedResponse}
										query={cannedQuery}
										responses={matchingCannedResponses}
										searchOnOpen={!slashTrigger}
									/>
								)}
								{copilotAvailable &&
									['active', 'waiting'].includes(selected.status) &&
									selected.assignedUserId === currentUserId && (
										<div className="copilot-assist" aria-live="polite">
											<div className="copilot-assist-toolbar">
												<div className="copilot-mode-actions">
													<button
														className="secondary-button copilot-suggest-button"
														disabled={
															copilotLoading ||
															Boolean(copilotSuggestion)
														}
														onClick={() =>
															void requestCopilot('suggest_reply')
														}
														type="button"
													>
														{copilotLoadingMode === 'suggest_reply'
															? 'Creating suggestion…'
															: 'Suggest reply'}
													</button>
													<button
														className="secondary-button"
														disabled={
															copilotLoading ||
															Boolean(copilotSuggestion)
														}
														onClick={() =>
															void requestCopilot('help_me_answer')
														}
														type="button"
													>
														{copilotLoadingMode === 'help_me_answer'
															? 'Finding answer…'
															: 'Help me answer'}
													</button>
													<button
														className="secondary-button"
														disabled={
															copilotLoading ||
															Boolean(copilotSuggestion)
														}
														onClick={() =>
															void requestCopilot(
																'conversation_summary',
															)
														}
														type="button"
													>
														{copilotLoadingMode ===
														'conversation_summary'
															? 'Summarising…'
															: 'Summarise'}
													</button>
													<button
														className="secondary-button"
														disabled={routingLoading || copilotLoading}
														onClick={() => void analyseRouting()}
														type="button"
													>
														{routingLoading
															? 'Analysing route…'
															: routingAnalysis?.stale
																? 'Analyse again'
																: 'Analyse routing'}
													</button>
												</div>
												<span>
													Private assistance — no reply or routing change
													is applied automatically
												</span>
											</div>
											{routingError && (
												<div className="copilot-error" role="status">
													{routingError}
												</div>
											)}
											{routingAnalysis && (
												<section
													aria-label="AI routing recommendation"
													className="routing-analysis-card"
												>
													<header>
														<div>
															<strong>Routing recommendation</strong>
															<span>
																{routingAnalysis.source ===
																'automatic'
																	? 'Automatic evaluation · '
																	: 'Manual analysis · '}
																{Math.round(
																	routingAnalysis.confidence *
																		100,
																)}
																% confidence
															</span>
														</div>
														{routingAnalysis.stale && (
															<span className="routing-stale">
																Conversation changed — analyse again
															</span>
														)}
													</header>
													<div className="routing-analysis-grid">
														<span>
															<small>Intent</small>
															<strong>
																{routingAnalysis.intent}
															</strong>
														</span>
														<span>
															<small>Language</small>
															<strong>
																{routingAnalysis.languageCode}
															</strong>
														</span>
														<span>
															<small>Sentiment</small>
															<strong>
																{routingValueLabel(
																	routingAnalysis.sentiment,
																)}
															</strong>
														</span>
														<span>
															<small>Suggested priority</small>
															<strong>
																{routingValueLabel(
																	routingAnalysis.suggestedPriority,
																)}
															</strong>
														</span>
													</div>
													<div className="routing-destination">
														<small>Recommended department</small>
														<strong>
															{routingAnalysis.recommendedDepartmentName ??
																'No department recommendation'}
														</strong>
														<p>{routingAnalysis.reason}</p>
													</div>
													{routingAnalysis.entities.length > 0 && (
														<ul
															aria-label="Detected entities"
															className="routing-entities"
														>
															{routingAnalysis.entities.map(
																(entity, index) => (
																	<li
																		key={`${entity.type}-${entity.value}-${index}`}
																	>
																		<strong>
																			{routingEntityLabel(
																				entity.type,
																			)}
																		</strong>
																		<span>{entity.value}</span>
																	</li>
																),
															)}
														</ul>
													)}
													{routingAnalysis.feedbackOutcome ? (
														<small className="routing-feedback-saved">
															Feedback saved:{' '}
															{routingValueLabel(
																routingAnalysis.feedbackOutcome,
															)}
															{routingAnalysis.feedbackPriority && (
																<>
																	{' '}
																	· priority corrected to{' '}
																	{routingValueLabel(
																		routingAnalysis.feedbackPriority,
																	)}
																</>
															)}
														</small>
													) : (
														<>
															<div className="copilot-suggestion-actions">
																<button
																	className="secondary-button"
																	disabled={
																		routingFeedbackBusy ||
																		routingAnalysis.stale
																	}
																	onClick={() =>
																		void submitRoutingFeedback(
																			'accepted',
																		)
																	}
																	type="button"
																>
																	Accurate
																</button>
																<button
																	className="secondary-button"
																	disabled={
																		routingFeedbackBusy ||
																		routingAnalysis.stale
																	}
																	onClick={() =>
																		setRoutingCorrectionOpen(
																			(open) => !open,
																		)
																	}
																	type="button"
																>
																	Correct
																</button>
																<button
																	className="secondary-button"
																	disabled={
																		routingFeedbackBusy ||
																		routingAnalysis.stale
																	}
																	onClick={() =>
																		void submitRoutingFeedback(
																			'dismissed',
																		)
																	}
																	type="button"
																>
																	Dismiss
																</button>
																<small>
																	Review only — use the existing
																	priority and Transfer controls
																	to apply changes.
																</small>
															</div>
															{routingCorrectionOpen && (
																<div className="routing-correction-form">
																	<label htmlFor="routing-correct-department">
																		Correct department (leave
																		blank if accurate)
																	</label>
																	<select
																		id="routing-correct-department"
																		onChange={(event) =>
																			setRoutingCorrectionDepartmentId(
																				event.currentTarget
																					.value,
																			)
																		}
																		value={
																			routingCorrectionDepartmentId
																		}
																	>
																		<option value="">
																			Department is accurate
																		</option>
																		{routingAnalysis.departmentOptions
																			.filter(
																				(department) =>
																					department.id !==
																					routingAnalysis.recommendedDepartmentId,
																			)
																			.map((department) => (
																				<option
																					key={
																						department.id
																					}
																					value={
																						department.id
																					}
																				>
																					{
																						department.name
																					}
																				</option>
																			))}
																	</select>
																	<label htmlFor="routing-correct-priority">
																		Correct priority (leave
																		blank if accurate)
																	</label>
																	<select
																		id="routing-correct-priority"
																		onChange={(event) =>
																			setRoutingCorrectionPriority(
																				event.currentTarget
																					.value as
																					| ''
																					| AiRoutingAnalysis['suggestedPriority'],
																			)
																		}
																		value={
																			routingCorrectionPriority
																		}
																	>
																		<option value="">
																			Priority is accurate
																		</option>
																		{(
																			[
																				'low',
																				'normal',
																				'high',
																				'urgent',
																			] as const
																		)
																			.filter(
																				(priority) =>
																					priority !==
																					routingAnalysis.suggestedPriority,
																			)
																			.map((priority) => (
																				<option
																					key={priority}
																					value={priority}
																				>
																					{routingValueLabel(
																						priority,
																					)}
																				</option>
																			))}
																	</select>
																	<button
																		className="primary-button"
																		disabled={
																			(!routingCorrectionDepartmentId &&
																				!routingCorrectionPriority) ||
																			routingFeedbackBusy
																		}
																		onClick={() =>
																			void submitRoutingFeedback(
																				'corrected',
																			)
																		}
																		type="button"
																	>
																		Save correction
																	</button>
																</div>
															)}
														</>
													)}
												</section>
											)}
											<form
												className="copilot-question-form"
												onSubmit={(event) => {
													event.preventDefault();
													void requestCopilot('ask_ai');
												}}
											>
												<label
													className="visually-hidden"
													htmlFor="copilot-question"
												>
													Ask AI about this conversation
												</label>
												<input
													disabled={
														copilotLoading || Boolean(copilotSuggestion)
													}
													id="copilot-question"
													maxLength={2_000}
													onChange={(event) =>
														setCopilotQuestion(event.target.value)
													}
													placeholder="Ask AI about this conversation…"
													type="text"
													value={copilotQuestion}
												/>
												<button
													className="secondary-button"
													disabled={
														copilotLoading ||
														Boolean(copilotSuggestion) ||
														!copilotQuestion.trim()
													}
													type="submit"
												>
													{copilotLoadingMode === 'ask_ai'
														? 'Asking…'
														: 'Ask AI'}
												</button>
											</form>
											{copilotError && (
												<div className="copilot-error" role="status">
													{copilotError}
												</div>
											)}
											{copilotSuggestion && (
												<div className="copilot-suggestion" role="region">
													<div className="copilot-suggestion-heading">
														<strong>
															{copilotSuggestion.mode ===
															'suggest_reply'
																? 'Suggested reply'
																: copilotSuggestion.mode ===
																	  'ask_ai'
																	? 'AI answer'
																	: copilotSuggestion.mode ===
																		  'conversation_summary'
																		? 'Conversation summary'
																		: 'Help me answer'}
														</strong>
														<small>
															Published company v
															{copilotSuggestion.tenantConfigVersion}{' '}
															· platform v
															{
																copilotSuggestion.platformConfigVersion
															}
														</small>
													</div>
													<p>{copilotSuggestion.message}</p>
													{copilotSuggestion.citations.length > 0 && (
														<ul
															className="copilot-citations"
															aria-label="Knowledge sources"
														>
															{copilotSuggestion.citations.map(
																(citation) => (
																	<li
																		key={`${citation.sourceId ?? citation.sourceTitle}-${citation.rank}`}
																	>
																		{citation.sourceUrl ? (
																			<a
																				href={
																					citation.sourceUrl
																				}
																				rel="noopener noreferrer"
																				target="_blank"
																			>
																				{
																					citation.sourceTitle
																				}
																			</a>
																		) : (
																			citation.sourceTitle
																		)}
																	</li>
																),
															)}
														</ul>
													)}
													<div className="copilot-suggestion-actions">
														{copilotSuggestion.mode ===
														'suggest_reply' ? (
															<>
																<button
																	className="primary-button"
																	disabled={
																		Boolean(reply.trim()) ||
																		usedCopilotSuggestion?.runId ===
																			copilotSuggestion.runId
																	}
																	onClick={useCopilotSuggestion}
																	type="button"
																>
																	{usedCopilotSuggestion?.runId ===
																	copilotSuggestion.runId
																		? 'In composer'
																		: 'Use suggestion'}
																</button>
																{usedCopilotSuggestion?.runId !==
																	copilotSuggestion.runId && (
																	<button
																		className="secondary-button"
																		onClick={
																			dismissCopilotSuggestion
																		}
																		type="button"
																	>
																		Dismiss
																	</button>
																)}
																{reply.trim() &&
																	usedCopilotSuggestion?.runId !==
																		copilotSuggestion.runId && (
																		<small>
																			Your existing draft is
																			preserved. Clear it
																			before using this
																			suggestion.
																		</small>
																	)}
															</>
														) : (
															<>
																<button
																	className="secondary-button"
																	disabled={
																		copilotFeedbackBusy ||
																		copilotFeedbackRunId ===
																			copilotSuggestion.runId
																	}
																	onClick={() =>
																		void recordCopilotGuidanceFeedback(
																			'helpful',
																		)
																	}
																	type="button"
																>
																	Helpful
																</button>
																<button
																	className="secondary-button"
																	disabled={
																		copilotFeedbackBusy ||
																		copilotFeedbackRunId ===
																			copilotSuggestion.runId
																	}
																	onClick={() =>
																		void recordCopilotGuidanceFeedback(
																			'unhelpful',
																		)
																	}
																	type="button"
																>
																	Not helpful
																</button>
																<button
																	className="secondary-button"
																	disabled={copilotFeedbackBusy}
																	onClick={
																		dismissCopilotSuggestion
																	}
																	type="button"
																>
																	Dismiss
																</button>
																{copilotFeedbackRunId ===
																	copilotSuggestion.runId && (
																	<small>Feedback sent</small>
																)}
															</>
														)}
													</div>
												</div>
											)}
										</div>
									)}
								<form
									className="reply-composer"
									onSubmit={(event) => void send(event)}
								>
									<button
										aria-expanded={cannedOpen}
										aria-label="Canned responses"
										className="canned-button"
										disabled={!canCompose}
										onClick={toggleCannedResponses}
										type="button"
									>
										⌁
									</button>
									<div className="reply-input-shell">
										<textarea
											aria-label="Reply"
											disabled={!canCompose}
											maxLength={10_000}
											onChange={(event) =>
												changeReply(
													event.currentTarget.value,
													event.currentTarget.selectionStart ??
														event.currentTarget.value.length,
												)
											}
											onKeyDown={replyKeyDown}
											onSelect={rememberSelection}
											placeholder={
												selected.status === 'closed'
													? 'Conversation closed'
													: canWhisper
														? 'Write a private whisper to the agent…'
														: canReply
															? 'Write a reply or type / for saved responses…'
															: 'Take over this chat to reply'
											}
											ref={replyRef}
											rows={1}
											value={reply}
										/>
										<div className="reply-input-actions">
											<div className="emoji-picker-wrap">
												<button
													aria-expanded={emojiOpen}
													aria-label="Choose an emoji"
													className="emoji-button"
													disabled={!canCompose}
													onClick={() => {
														rememberSelection();
														closeCannedResponses();
														setEmojiOpen((open) => !open);
													}}
													type="button"
												>
													<svg aria-hidden="true" viewBox="0 0 24 24">
														<circle cx="12" cy="12" r="9" />
														<path d="M8.5 14.5c1 1.2 2.1 1.8 3.5 1.8s2.5-.6 3.5-1.8" />
														<path d="M9 9.5h.01M15 9.5h.01" />
													</svg>
												</button>
												{emojiOpen && (
													<div
														className="emoji-picker"
														role="listbox"
														aria-label="Emojis"
													>
														{CHAT_EMOJIS.map((emoji) => (
															<button
																aria-label={`Insert ${emoji}`}
																key={emoji}
																onClick={() => chooseEmoji(emoji)}
																type="button"
															>
																{emoji}
															</button>
														))}
													</div>
												)}
											</div>
											<button
												aria-busy={sending}
												aria-label={
													canWhisper
														? sending
															? 'Sending whisper'
															: 'Whisper'
														: sending
															? 'Sending reply'
															: 'Send reply'
												}
												className={`send-message-button${canWhisper ? ' whisper-button' : ''}`}
												disabled={busy || !canCompose || !reply.trim()}
											>
												{sending ? (
													<span
														aria-hidden="true"
														className="send-button-spinner"
													/>
												) : canWhisper ? (
													'Whisper'
												) : (
													<svg aria-hidden="true" viewBox="0 0 24 24">
														<path d="M4 12h15M13 6l6 6-6 6" />
													</svg>
												)}
											</button>
										</div>
									</div>
								</form>
							</div>
						</section>
					</div>
					<aside
						className="visitor-detail-panel"
						aria-labelledby="visitor-details-heading"
					>
						<header>
							<div>
								<span>Visitor</span>
								<h3 id="visitor-details-heading">
									{selected.visitorName ?? visitor?.name ?? 'Visitor'}
								</h3>
							</div>
						</header>
						{visitorLoading && !visitor ? (
							<div className="visitor-detail-loading">Loading visitor details…</div>
						) : (
							<>
								{visitorError && <div className="alert error">{visitorError}</div>}
								{hasVisitorDetails && (
									<dl className="visitor-detail-list">
										{selectedVisitorCompany && (
											<div>
												<dt>Company</dt>
												<dd>{selectedVisitorCompany}</dd>
											</div>
										)}
										{visitorEmail && (
											<div>
												<dt>Email</dt>
												<dd>{visitorEmail}</dd>
											</div>
										)}
										{visitor?.phone && (
											<div>
												<dt>Phone</dt>
												<dd>{visitor.phone}</dd>
											</div>
										)}
										{visitor?.notes && (
											<div>
												<dt>Notes</dt>
												<dd>{visitor.notes}</dd>
											</div>
										)}
										{visitor?.variables.map((variable) => (
											<div key={variable.name}>
												<dt>{variable.title}</dt>
												<dd>
													{variable.url ? (
														<a
															href={variable.url}
															rel="noopener noreferrer"
															target="_blank"
														>
															{variable.value}
														</a>
													) : (
														variable.value
													)}
												</dd>
											</div>
										))}
									</dl>
								)}
								<div className="visitor-stat-grid">
									<div>
										<strong>{visitor?.visitCount ?? '—'}</strong>
										<span>Visits</span>
									</div>
									<div>
										<strong>{visitor?.chatCount ?? '—'}</strong>
										<span>Chats</span>
									</div>
								</div>
								<button
									className="secondary-button visitor-history-button"
									onClick={() => onViewVisitorHistory(selected.visitorId)}
									type="button"
								>
									View this visitor’s chat history
								</button>
								<div className="visitor-location-card">
									<div className="visitor-location-heading">
										<span
											className="visitor-location"
											title={
												visitor
													? visitorLocationLabel(visitor)
													: 'Location unavailable'
											}
										>
											<CountryFlag countryCode={visitor?.countryCode} />
										</span>
										<div>
											<strong>
												{visitor
													? visitorLocationLabel(visitor)
													: 'Location unavailable'}
											</strong>
											<code>{visitor?.ipAddress ?? 'IP unavailable'}</code>
										</div>
									</div>
									<VisitorLocationMap
										countryCode={visitor?.countryCode}
										label={
											visitor
												? visitorLocationLabel(visitor)
												: 'visitor location'
										}
										latitude={visitor?.latitude}
										longitude={visitor?.longitude}
									/>
								</div>
								{aiRatingReview && (
									<AiChatRatingReviewCard review={aiRatingReview} />
								)}
							</>
						)}
					</aside>
				</div>
			)}
			{conversationAction && selected && (
				<div className="dialog-backdrop" role="presentation">
					{conversationAction === 'block' && (
						<section
							aria-labelledby="block-visitor-heading"
							aria-modal="true"
							className="dialog-card conversation-action-dialog"
							role="dialog"
						>
							<h3 id="block-visitor-heading">Block visitor?</h3>
							<p>
								This closes the chat and blocks{' '}
								<strong>{selected.visitorName ?? 'this visitor'}</strong> using
								their visitor identity and latest known IP address.
							</p>
							{conversationActionError && (
								<div className="alert error">{conversationActionError}</div>
							)}
							<div className="dialog-actions">
								<button
									className="secondary-button"
									disabled={conversationActionBusy}
									onClick={() => setConversationAction(undefined)}
									type="button"
								>
									Cancel
								</button>
								<button
									className="danger-button"
									disabled={conversationActionBusy}
									onClick={() => void confirmBlockVisitor()}
									type="button"
								>
									{conversationActionBusy ? 'Blocking…' : 'Block Visitor'}
								</button>
							</div>
						</section>
					)}
					{conversationAction === 'email' && (
						<form
							aria-labelledby="email-conversation-heading"
							aria-modal="true"
							className="dialog-card conversation-action-dialog"
							onSubmit={(event) => void emailConversation(event)}
							role="dialog"
						>
							<h3 id="email-conversation-heading">Email Conversation</h3>
							<p>Send a readable transcript of this conversation by email.</p>
							<label>
								<span>Email address</span>
								<input
									autoFocus
									disabled={conversationActionBusy}
									onChange={(event) =>
										setTranscriptEmail(event.currentTarget.value)
									}
									required
									type="email"
									value={transcriptEmail}
								/>
							</label>
							{conversationActionError && (
								<div className="alert error">{conversationActionError}</div>
							)}
							<div className="dialog-actions">
								<button
									className="secondary-button"
									disabled={conversationActionBusy}
									onClick={() => setConversationAction(undefined)}
									type="button"
								>
									Cancel
								</button>
								<button
									className="primary-button"
									disabled={conversationActionBusy}
								>
									{conversationActionBusy ? 'Queueing…' : 'Send Email'}
								</button>
							</div>
						</form>
					)}
					{conversationAction === 'push' && (
						<form
							aria-labelledby="push-visitor-heading"
							aria-modal="true"
							className="dialog-card conversation-action-dialog"
							onSubmit={(event) => void pushVisitor(event)}
							role="dialog"
						>
							<h3 id="push-visitor-heading">Send To Page</h3>
							<p>The visitor’s current browser tab will be redirected to this URL.</p>
							<label>
								<span>Destination URL</span>
								<input
									autoFocus
									disabled={conversationActionBusy}
									onChange={(event) => setPushUrl(event.currentTarget.value)}
									placeholder="https://www.example.com/page"
									required
									type="url"
									value={pushUrl}
								/>
							</label>
							{conversationActionError && (
								<div className="alert error">{conversationActionError}</div>
							)}
							<div className="dialog-actions">
								<button
									className="secondary-button"
									disabled={conversationActionBusy}
									onClick={() => setConversationAction(undefined)}
									type="button"
								>
									Cancel
								</button>
								<button
									className="primary-button"
									disabled={conversationActionBusy}
								>
									{conversationActionBusy ? 'Sending…' : 'Push Visitor'}
								</button>
							</div>
						</form>
					)}
				</div>
			)}
		</div>
	);
}
